import { LayoutModule } from '@angular/cdk/layout';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { BehaviorSubject } from 'rxjs';

import { UI_ICONS_LANGUAGE_TOKEN, UiIconsModule } from 'ui-icons';

import {
  UiPlatformActionTickerModule,
  UiPlatformButtonComponent,
  UiPlatformCheckboxComponent,
  UiPlatformDatepickerComponent,
  UiPlatformDrawerComponent,
  UiPlatformInputComponent,
  UiPlatformMenuComponent,
  UiPlatformMessageModule,
  UiPlatformProgressIndicatorsModule,
  UiPlatformRangepickerComponent,
  UiPlatformSelectComponent,
  UiPlatformStatusMarkerModule,
  UiPlatformSwitchComponent,
  UiPlatformTabsModule
} from 'ui-platform';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ButtonsComponent } from './buttons/buttons.component';
import { ColorsComponent } from './colors/colors.component';
import { ComponentsComponent } from './components/components.component';
import { DialogModalComponent, ExampleDialogComponent, ExampleSnackBarComponent } from './dialog-modal/dialog-modal.component';
import { FormControlsComponent } from './form-controls/form-controls.component';
import { IconsComponent } from './icons/icons.component';
import { LayoutComponent } from './layout/layout.component';
import { NavigationComponent } from './navigation/navigation.component';
import { NewColorsComponent } from './new-colors/new-colors.component';
import { NewTypographyComponent } from './new-typography/new-typography.component';
import { PlatformActionTickerComponent } from './platform-action-ticker/platform-action-ticker.component';
import { PlatformButtonComponent } from './platform-button/platform-button.component';
import { PlatformCheckboxComponent } from './platform-checkbox/platform-checkbox.component';
import { PlatformDatepickerComponent } from './platform-datepicker/platform-datepicker.component';
import { PlatformDrawerComponent } from './platform-drawer/platform-drawer.component';
import { PlatformInputComponent } from './platform-input/platform-input.component';
import { PlatformMenuComponent } from './platform-menu/platform-menu.component';
import { PlatformMessageComponent } from './platform-message/platform-message.component';
import { PlatformProgressIndicatorComponent } from './platform-progress-indicator/platform-progress-indicator.component';
import { PlatformRadioComponent } from './platform-radio/platform-radio.component';
import { PlatformRangepickerComponent } from './platform-rangepicker/platform-rangepicker.component';
import { PlatformSelectComponent } from './platform-select/platform-select.component';
import { PlatformStateIndicatorComponent } from './platform-state-indicator/platform-state-indicator.component';
import { PlatformStatusMarkerComponent } from './platform-status-marker/platform-status-marker.component';
import { PlatformSwitchComponent } from './platform-switch/platform-switch.component';
import { PlatformTabsComponent } from './platform-tabs/platform-tabs.component';
import { TypographyComponent } from './typography/typography.component';

@NgModule({
  declarations: [
    AppComponent,
    ColorsComponent,
    ComponentsComponent,
    DialogModalComponent,
    ExampleDialogComponent,
    ExampleSnackBarComponent,
    FormControlsComponent,
    TypographyComponent,
    NavigationComponent,
    LayoutComponent,
    ButtonsComponent,
    IconsComponent,
    PlatformButtonComponent,
    PlatformMenuComponent,
    PlatformDrawerComponent,
    PlatformSwitchComponent,
    PlatformProgressIndicatorComponent,
    PlatformStateIndicatorComponent,
    PlatformInputComponent,
    PlatformSelectComponent,
    PlatformCheckboxComponent,
    PlatformStatusMarkerComponent,
    NewTypographyComponent,
    NewColorsComponent,
    PlatformActionTickerComponent,
    PlatformDatepickerComponent,
    PlatformMessageComponent,
    PlatformTabsComponent,
    PlatformRangepickerComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    // *** Angular modules ***
    BrowserAnimationsModule,
    ReactiveFormsModule,
    // *** 3th party ***
    LayoutModule,
    MatBadgeModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatNativeDateModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSidenavModule,
    // *** Routing module ***
    AppRoutingModule,
    UiIconsModule,
    UiPlatformButtonComponent,
    UiPlatformProgressIndicatorsModule,
    UiPlatformInputComponent,
    UiPlatformSelectComponent,
    UiPlatformSwitchComponent,
    UiPlatformMenuComponent,
    UiPlatformDrawerComponent,
    UiPlatformCheckboxComponent,
    UiPlatformStatusMarkerModule,
    UiPlatformActionTickerModule,
    UiPlatformDatepickerComponent,
    UiPlatformMessageModule,
    UiPlatformTabsModule,
    FormsModule,
    PlatformRadioComponent,
    UiPlatformRangepickerComponent
  ],
  providers: [
    {
      provide: UI_ICONS_LANGUAGE_TOKEN,
      useFactory: () => new BehaviorSubject('ar')
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
